import type { JSX, SVGProps } from "react";

/**
 * Lucide-style stroke-width-2 icons used by the chat UI.
 * Per cycledesign B2B icon weighting: stroke-width 2.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 16, children, ...rest }: IconProps & { children: JSX.Element }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconPlus(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    )
  });
}

export function IconChevron(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: <polyline points="6 9 12 15 18 9" />
  });
}

export function IconSettings(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </>
    )
  });
}

export function IconGrid(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
      </>
    )
  });
}

export function IconSpark(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17l-1.9-5.1L4.5 10l5.6-1.4L12 3z" />
  });
}

export function IconUsers(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    )
  });
}

export function IconChart(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M3 3v18h18" />
        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
      </>
    )
  });
}

export function IconFile(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    )
  });
}

export function IconMessage(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  });
}

export function IconPaperclip(props: IconProps): JSX.Element {
  // Lucide paperclip — used for the attach/upload affordance.
  return base({
    ...props,
    children: (
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    )
  });
}

export function IconArrowRight(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </>
    )
  });
}

export function IconArrowDown(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="5 12 12 19 19 12" />
      </>
    )
  });
}

export function IconCopy(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </>
    )
  });
}

export function IconThumbUp(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M7 10v12" />
        <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
      </>
    )
  });
}

export function IconThumbDown(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M17 14V2" />
        <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
      </>
    )
  });
}

export function IconRefresh(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.36 2.64L3 8" />
        <path d="M3 3v5h5" />
      </>
    )
  });
}

export function IconHeadset(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
        <rect x="2" y="14" width="5" height="7" rx="1" />
        <rect x="17" y="14" width="5" height="7" rx="1" />
      </>
    )
  });
}

export function IconTrash(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      </>
    )
  });
}

export function IconStop(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: <rect x="6" y="6" width="12" height="12" rx="2" />
  });
}

export function IconGlobe(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </>
    )
  });
}

export function IconClock(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    )
  });
}

export function IconUpload(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </>
    )
  });
}

export function IconGitBranch(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <line x1="6" y1="3" x2="6" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
      </>
    )
  });
}

export function IconFlag(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </>
    )
  });
}

export function IconActivity(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  });
}

export function IconWrench(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    )
  });
}

export function IconBell(props: IconProps): JSX.Element {
  return base({
    ...props,
    children: (
      <>
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </>
    )
  });
}
